import * as React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import DatePicker from "../../../Components/DatePicker";
import dayjs from "dayjs";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

export default function AddHoliday({ open, setOpen, selectedDate, onAddHoliday }) {
  const [date, setDate] = useState(null); // Initialize state for the date
  const [occasion, setOccasion] = React.useState("");
  const [dateError, setDateError] = React.useState("");
  const [occasionError, setOccasionError] = React.useState("");

  const handleDateChange = (newDate) => {
    setDate(newDate);
  };

  const handleClickOpen = () => {
    if (setOpen) setOpen(true);
  };

  const handleClose = () => {
    if (setOpen) setOpen(false);
    // Reset form fields
    setOccasion("");
    setDateError(false);
    setOccasionError(false);
  };

  // Update date when selectedDate prop changes
  useEffect(() => {
    if (selectedDate) {
      setDate(dayjs(selectedDate));
    }
  }, [selectedDate]);

  const handleSave = () => {
    // Validate fields
    let hasError = false;

    if (!date) {
      setDateError(true);
      hasError = true;
    }

    if (!occasion || occasion.trim() === "") {
      setOccasionError(true);
      hasError = true;
    }

    if (hasError) return;
    if (onAddHoliday && date && occasion) {
      onAddHoliday(occasion, date.toDate());
    }
    setDateError(false);
    setOccasionError(false);
    setOccasion("");
    handleClose();
  };

  // Define reusable input styling to match Department style
  const inputProps = {
    sx: {
      bgcolor: "#f9f9f9",
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: "#e0e0e0",
        },
        "&:hover fieldset": {
          borderColor: "#bdbdbd",
        },
      },
      "& .MuiSelect-select": {
        color: "#424242", // For dropdown selects
      },
      "& .MuiInputBase-input": {
        color: "#424242", // For text and date inputs
      },
    },
  };

  return (
    <div>
      <React.Fragment>
        <Stack direction="row" spacing={2} sx={{ margin: "20px 0 5px 2px" }}>
          <Box
            onClick={handleClickOpen}
            sx={{
              backgroundColor: "#4285F4",
              "&:hover": { backgroundColor: "#3367D6" },
              textAlign: "center",
              padding: "10px",
              marginLeft: "10px",
              height: "40px",
              width: "125px",
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              borderRadius: "4px",
              fontSize: "15px",
              fontFamily: "sans-serif",
              cursor: "pointer",
              color: "#fff",
            }}
          >
            <AddIcon />
            Add Holiday
          </Box>
        </Stack>
        <Dialog
          fullWidth
          maxWidth="md"
          open={open}
          onClose={handleClose}
          TransitionComponent={Transition}
          PaperProps={{
            style: {
              position: "absolute",
              top: 0,
              right: 0,
              width: "calc(100% - 260px)", // Adjusted width to account for sidebar
              height: "100%",
              backgroundColor: "#f5f5f5",
              margin: 0,
              maxHeight: "100%",
              borderRadius: 0,
              overflowX: "hidden",
              zIndex: 1200,
            },
          }}
        >
          <AppBar sx={{ position: "relative", bgcolor: "#ffffff", boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)" }}>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleClose}
                aria-label="close"
                sx={{ color: "#212121" }}
              >
                <CloseIcon fontSize="large" />
              </IconButton>
              <Typography
                sx={{ ml: 2, flex: 1, fontSize: "18px", color: "#212121" }}
                component="div"
              >
                New Holiday
              </Typography>
            </Toolbar>
          </AppBar>
          <Box sx={{ background: "#f5f5f5", p: 4, overflowY: "auto" }}>
            <Card sx={{ p: 2, bgcolor: "#ffffff", boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)" }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: "#212121", mb: 3 }}>
                  Add Holiday
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Occasion <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Holiday Occasion"
                      size="small"
                      value={occasion}
                      onChange={(e) => setOccasion(e.target.value)}
                      InputProps={inputProps}
                      error={occasionError}
                      helperText={occasionError ? "Occasion is required" : ""}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body1" mb={1} sx={{ color: "#212121" }}>
                      Date <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={date}
                        onChange={handleDateChange}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            error: dateError,
                            helperText: dateError ? "Date is required" : "",
                            InputProps: inputProps
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>

                <Box display="flex" gap={2} mt={3}>
                  <Button
                    onClick={handleSave}
                    variant="contained"
                    sx={{
                      bgcolor: "#4285F4",
                      color: "white",
                      "&:hover": { bgcolor: "#3367D6" },
                      fontWeight: 500,
                      textTransform: "none",
                      padding: "8px 16px",
                    }}
                  >
                    Add
                  </Button>
                  <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                      borderColor: "#bdbdbd",
                      color: "#424242",
                      "&:hover": {
                        borderColor: "#9e9e9e",
                        bgcolor: "rgba(0, 0, 0, 0.04)",
                      },
                      textTransform: "none",
                      padding: "8px 16px",
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Dialog>
      </React.Fragment>
    </div>
  );
}
